import {inject, injectable} from 'inversify';
import {makeAutoObservable, reaction} from 'mobx';

import type {CoreUserProfileUpdateParams} from '../../../../api/types/core/methods';
import type {Nullable} from '../../../../utils/utility-types';
import type {FileModel} from '../../../domain/File/File.model';
import type {UserModel} from '../../../domain/User/User.model';
import type {CurrentUserService} from '../CurrentUser.service';

import {categoryStore} from '../../../../api/category';
import {membershipStore} from '../../../../api/membership';
import {currentUserStore} from '../../../../api/user';
import {ReactionDisposeManager} from '../../../../utils/store';
import {CategoryMapperJrpc} from '../../../domain/Category/mappers/Category.mapper.jrpc';
import {UserMapperJrpc} from '../../../domain/User/mappers/User.mapper.jrpc';

@injectable()
export class CurrentUserServiceJrpc implements CurrentUserService {
  disposer = new ReactionDisposeManager();
  user: Nullable<UserModel> = null;

  constructor(
    @inject(CategoryMapperJrpc)
    private readonly categoryMapper: CategoryMapperJrpc,
    @inject(UserMapperJrpc) private readonly userMapper: UserMapperJrpc,
  ) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.setupReactions();
  }

  get id() {
    return this.user?.id || '';
  }

  async fetch() {
    return currentUserStore.fetch();
  }

  getCategoryById(id: string) {
    const props = this.categoryStore.categoryLookup.get(id);

    if (!props) {
      return null;
    }

    return this.categoryMapper.mapDataToModel(props);
  }

  getIsMeFrozenForCourse(courseId: string) {
    const membership = membershipStore.membershipLookup.get(courseId || '');

    return membership ? membership.status === 'frozen' : false;
  }

  async hasActiveUser() {
    return this.currentUserStore.userExists();
  }

  hasRole(role: UserModel.Role) {
    return Boolean(this.currentUserStore.user?.roles?.includes(role));
  }

  isCoachForEntity(entity: CurrentUserService.CheckIsMeCoachTarget) {
    return Boolean(entity?.coach_ids?.includes(this.id));
  }

  isMyFile(file: FileModel) {
    return this.user?.id === file.userId;
  }

  async update(user: UserModel) {
    const dto = this.userMapper.mapModelToDto(user);

    // slug should be unique, so we don't need to update it if it's the same
    if (this.user?.slug === user.slug) {
      delete dto.slug;
    }

    await this.currentUserStore.update({
      b2b: dto,
      core: dto as CoreUserProfileUpdateParams,
    });
  }

  private get categoryStore() {
    return categoryStore;
  }

  private get currentUserStore() {
    return currentUserStore;
  }

  private setupReactions() {
    this.disposer.watch(
      'user_change',
      reaction(
        () => currentUserStore.user,
        user => {
          this.user = user ? this.userMapper.mapDataToModel(user) : null;
        },
        {
          fireImmediately: true,
        },
      ),
    );
  }
}
